/* eslint-disable camelcase */
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
} from '@chakra-ui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';

import { useGetAssignmentTypesQuery } from '@/API/assignments.api';
import { useGetPersonnelTypesQuery } from '@/API/personnel.api';
import {
  useCreateFilterMutation,
  useCreateThemeMutation,
  useCreateViewMutation,
  useUpdateFilterMutation,
  useUpdateThemeMutation,
  useUpdateViewMutation,
} from '@/API/views.api';
import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog';
import DrawerActionButtons from '@/components/drawer-action-buttons/DrawerActionButtons';
import ViewEditorAssignmentsTallies from '@/components/view-editor/ViewEditorAssignmentsTallies';
import ViewEditorBasicProperties from '@/components/view-editor/ViewEditorBasicProperties';
import ViewEditorPersonnel from '@/components/view-editor/ViewEditorPersonnel';
import ViewEditorViewerConfiguration from '@/components/view-editor/ViewEditorViewerConfiguration';
import styles from '@/components/view-editor-drawer/styles.module.scss';
import UIConfig from '@/config/ui.config';
import { FEATURE_FLAGS_ENUM } from '@/constants/config';
import {
  DEFAULT_CREATE_SUCCESS_MESSAGE,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_TOAST_DURATION,
  DEFAULT_UPDATE_SUCCESS_MESSAGE,
  EMPTY_ARRAY_LENGTH,
  ToastTypes,
  VIEW_EDITOR_DRAWER_HEADER_Z_INDEX,
} from '@/constants/defaults';
import { ViewEditorDrawerMode, ViewEditorDrawerTabIndices } from '@/constants/enums';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setViewEditorAdvancedSortingRulesIsEnabled,
  setViewEditorDraftAsOriginal,
  setViewEditorDraftAssignmentTypes,
  setViewEditorDraftDepartmentIds,
  setViewEditorDraftMultiSortListLayout,
  setViewEditorDraftNavIsBlocked,
  setViewEditorDraftPersonnelTypes,
  setViewEditorDraftTemplateIds,
  setViewEditorShowConfirmSaveChanges,
  setViewEditorTabIndex,
} from '@/store/slices/viewEditor.slice';
import { RootState } from '@/store/store';
import { ELEMENT_DATA_TEST_IDS } from '@/tests/testConstants';
import { ApiError } from '@/types/api.types';
import { ViewLayoutType } from '@/types/view.types';

interface ViewEditorDrawerProps {
  onClose: () => void;
  onSave: (viewId: number) => void;
}

const ViewEditorDrawer = (props: ViewEditorDrawerProps): React.JSX.Element => {
  const { onClose, onSave } = props;

  const { data: assignmentTypes, isSuccess: getAssignmentTypesSuccess } = useGetAssignmentTypesQuery();
  const { data: personnelTypes, isSuccess: getPersonnelTypesSuccess } = useGetPersonnelTypesQuery();

  const [updateFilter] = useUpdateFilterMutation();
  const [updateTheme] = useUpdateThemeMutation();
  const [updateView] = useUpdateViewMutation();

  const [createFilter] = useCreateFilterMutation();
  const [createTheme] = useCreateThemeMutation();
  const [createView] = useCreateViewMutation();

  const { isLoading, isOpen, mode, navIsBlocked, showConfirmSaveChanges, tabIndex } = useAppSelector(
    (state: RootState) => state.viewEditor.uiState,
  );

  const {
    uiState: {
      assignmentTypes: assignmentTypesOptions,
      personnelTypes: personnelTypesOptions,
      selectedDepartments,
      selectedTemplates,
    },
    viewDraft,
    viewOriginal,
  } = useAppSelector((state: RootState) => state.viewEditor);

  const dispatch = useAppDispatch();

  const flags = useFlags();
  const toast = useToast();

  const shiftsToDisplayFeatureEnabled = flags[FEATURE_FLAGS_ENUM.LV14123DailyListViewHideSections];

  React.useEffect(() => {
    const showAdvancedSortingRulesFlag = flags[FEATURE_FLAGS_ENUM.LV10019ListViewSorting];
    dispatch(setViewEditorAdvancedSortingRulesIsEnabled(showAdvancedSortingRulesFlag));

    const isListLayout = viewDraft.theme.data.layout === ViewLayoutType.LIST;

    if (showAdvancedSortingRulesFlag) {
      dispatch(setViewEditorDraftMultiSortListLayout(isListLayout));
    }
  }, [dispatch, flags, viewDraft.theme.data.layout]);

  React.useEffect(() => {
    if (getPersonnelTypesSuccess && !personnelTypesOptions.length)
      dispatch(setViewEditorDraftPersonnelTypes(personnelTypes));
  }, [dispatch, getPersonnelTypesSuccess, personnelTypes, personnelTypesOptions.length]);

  React.useEffect(() => {
    if (getAssignmentTypesSuccess && !assignmentTypesOptions.length)
      dispatch(setViewEditorDraftAssignmentTypes(assignmentTypes));
  }, [assignmentTypes, assignmentTypesOptions.length, dispatch, getAssignmentTypesSuccess]);

  const hasValidShiftsToDisplay = React.useMemo(() => {
    // If feature flag is disabled, always valid
    if (!shiftsToDisplayFeatureEnabled) {
      return true;
    }

    const isListLayout = viewDraft.theme.data.layout === ViewLayoutType.LIST;
    const shiftsToDisplay = viewDraft.theme.data.shiftsToDisplay;

    // undefined/null is valid (to support existing views as backend will default the value to undefined/null)
    // non-empty array is valid (user must select at least one display shift section for list views)
    const isShiftsToDisplayValid =
      shiftsToDisplay === undefined ||
      shiftsToDisplay === null ||
      shiftsToDisplay.length > EMPTY_ARRAY_LENGTH;
      
    // Validation logic:
    // - For non-list layouts, always valid
    // - For list layouts:
    //   - undefined/null is valid (will default to all sections selected to support existing views and prevent accidental data loss)
    //   - empty array [] is invalid (user explicitly cleared all - error message is displayed)
    //   - non-empty array is valid
    return !isListLayout || isShiftsToDisplayValid;
  }, [shiftsToDisplayFeatureEnabled, viewDraft.theme.data.layout, viewDraft.theme.data.shiftsToDisplay]);

  React.useEffect(() => {
    const allRequiredFieldsSatisfied = [
      selectedDepartments.length,
      selectedTemplates.length,
      viewDraft.name.length >= UIConfig.MINIMUM_VIEW_NAME_LENGTH,
      viewDraft.name.length <= UIConfig.MAXIMUM_VIEW_NAME_LENGTH,
      hasValidShiftsToDisplay,
    ].every(Boolean);
    if (!allRequiredFieldsSatisfied) {
      dispatch(setViewEditorDraftNavIsBlocked(true));
    } else {
      dispatch(setViewEditorDraftNavIsBlocked(false));
    }
  }, [
    dispatch,
    selectedDepartments.length,
    selectedTemplates.length,
    viewDraft.name.length,
    hasValidShiftsToDisplay,
  ]);

  const isDirty = () => {
    return !_.isEqual(viewOriginal, viewDraft);
  };

  const handleClose = (bypassConfirm = false) => {
    if (isDirty() && !bypassConfirm) {
      dispatch(setViewEditorShowConfirmSaveChanges(true));
    } else {
      onClose();
    }
  };

  const handleCloseWithoutSaving = () => {
    dispatch(setViewEditorShowConfirmSaveChanges(false));
    onClose();
  };

  const addSuccessToast = (message: string) => {
    toast({
      duration: DEFAULT_TOAST_DURATION,
      isClosable: true,
      position: 'top',
      status: ToastTypes.SUCCESS,
      title: message,
    });
  };

  const addErrorToast = (message?: string) => {
    toast({
      duration: DEFAULT_TOAST_DURATION,
      isClosable: true,
      position: 'top',
      status: ToastTypes.ERROR,
      title: message || DEFAULT_ERROR_MESSAGE,
    });
  };

  const closeConfirmSaveChangesDialog = () => {
    dispatch(setViewEditorShowConfirmSaveChanges(false));
  };

  // Save should be disabled in Creation mode unless you are in the final tab
  const notLastCreationStep =
    mode === ViewEditorDrawerMode.CREATE && tabIndex !== ViewEditorDrawerTabIndices.VIEWER_CONFIGURATION;

  const handleSave = async () => {
    if (!isDirty) handleClose(true);

    // if opened, close the Confirm dialog
    if (showConfirmSaveChanges) {
      closeConfirmSaveChangesDialog();
    }

    // Prepare theme data - only include shiftsToDisplay for LIST layout with feature flag enabled
    const isListLayout = viewDraft.theme.data.layout === ViewLayoutType.LIST;
    const { shiftsToDisplay, ...restThemeData } = viewDraft.theme.data;
    const themeDataToSave = {
      ...restThemeData,
      ...(isListLayout && shiftsToDisplayFeatureEnabled ? { shiftsToDisplay } : {}),
    };

    if (mode === ViewEditorDrawerMode.CREATE && !navIsBlocked) {
      // Create new view
      try {
        if (!viewDraft?.filter || !viewDraft?.theme) return;
        const filterResponse = await createFilter({
          filterData: viewDraft.filter,
          filterName: `${viewDraft.name} Filter`,
        }).unwrap();

        const themeResponse = await createTheme({
          themeData: themeDataToSave,
          themeName: `${viewDraft.name} Theme`,
        }).unwrap();

        if (filterResponse.filter_id && themeResponse.theme_id) {
          const viewResponse = await createView({
            ...viewDraft,
            filterId: filterResponse.filter_id,
            themeId: themeResponse.theme_id,
          }).unwrap();

          const createViewHasError = !filterResponse.filter_id || !themeResponse.theme_id || !viewResponse.view_id;

          if (createViewHasError) {
            addErrorToast();
          } else {
            addSuccessToast(DEFAULT_CREATE_SUCCESS_MESSAGE);
          }
          if (viewResponse.view_id && tabIndex === ViewEditorDrawerTabIndices.VIEWER_CONFIGURATION) {
            onSave(viewResponse.view_id);

            return;
          }

          dispatch(setViewEditorDraftAsOriginal());
        }
      } catch (error) {
        // Cast unknown error type to ApiError
        const apiError = error as ApiError;
        const message = apiError?.data?.Message ?? DEFAULT_ERROR_MESSAGE;
        addErrorToast(message);
      }
    } else {
      // Edit existing view
      // If no view, bail
      if (!viewDraft) return;

      try {
        await updateFilter(viewDraft.filter).unwrap();

        const { name, theme_id } = viewDraft.theme;

        await updateTheme({
          data: themeDataToSave,
          name,
          // eslint-disable-next-line camelcase
          theme_id,
        }).unwrap();

        await updateView({
          accessibleBy: viewDraft.accessibleBy,
          filterId: viewDraft.filterId,
          name: viewDraft.name,
          themeId: viewDraft.themeId,
          viewId: viewDraft.viewId,
        }).unwrap();

        addSuccessToast(DEFAULT_UPDATE_SUCCESS_MESSAGE);

        if (viewDraft.viewId && tabIndex === ViewEditorDrawerTabIndices.VIEWER_CONFIGURATION) {
          onSave(viewDraft.viewId);
          return;
        }

        dispatch(setViewEditorDraftAsOriginal());
      } catch (error) {
        // Cast unknown error type to ApiError
        const apiError = error as ApiError;
        const message = apiError?.data?.Message ?? DEFAULT_ERROR_MESSAGE;
        addErrorToast(message);
      }
    }
  };

  if (!viewDraft) return <></>;

  const handleDepartmentChange = (departments: number[]) => {
    dispatch(setViewEditorDraftDepartmentIds(departments));
  };

  // Disable save button if form has errors or the user has not navigated to the last step
  const isSaveDisabled = navIsBlocked || notLastCreationStep;

  const getSaveConfirmationDialog = (): React.JSX.Element => {
    if (isSaveDisabled && mode === ViewEditorDrawerMode.CREATE) {
      return (
        <ConfirmationDialog
          closeButtonLabel="Close"
          closeConfirmDialog={() => dispatch(setViewEditorShowConfirmSaveChanges(false))}
          confirmButtonLabel="Continue Create"
          description="Are you sure you want to close without creating the view?"
          handleClose={handleCloseWithoutSaving}
          handleConfirm={closeConfirmSaveChangesDialog}
          isOpen={showConfirmSaveChanges}
          title="Confirm Close"
        />
      );
    }

    return (
      <ConfirmationDialog
        closeButtonLabel="Close"
        closeConfirmDialog={() => dispatch(setViewEditorShowConfirmSaveChanges(false))}
        confirmButtonLabel="Save"
        description="Are you sure you want to close without saving?"
        handleClose={handleCloseWithoutSaving}
        handleConfirm={handleSave}
        isOpen={showConfirmSaveChanges}
        title="Confirm Close"
      />
    );
  };

  return (
    <Drawer
      blockScrollOnMount={true}
      isOpen={isOpen}
      onClose={handleClose}
      placement={'right'}
      preserveScrollBarGap={false}
      returnFocusOnClose={false}
      size={'lg'}
    >
      {getSaveConfirmationDialog()}

      <DrawerOverlay />
      <DrawerContent maxW={'1300px'} w={'1300px'}>
        {mode === ViewEditorDrawerMode.EDIT ? (
          <DrawerHeader data-testid={`${ELEMENT_DATA_TEST_IDS.VIEW_EDITOR_DRAWER_HEADER}-${viewDraft.viewId}`}>
            Editing View - {viewDraft.name}
          </DrawerHeader>
        ) : (
          <DrawerHeader data-testid={ELEMENT_DATA_TEST_IDS.CREATE_VIEW_EDITOR_DRAWER_HEADER}>
            Creating New View
          </DrawerHeader>
        )}
        <DrawerBody pt={0} overflowX="auto" pl={1}>
          {isLoading ? (
            <Box textAlign={'center'}>
              <Spinner color="blue.500" emptyColor="gray.200" size="xl" speed="0.65s" thickness="4px" />
            </Box>
          ) : (
            <Tabs isLazy index={tabIndex} onChange={(index) => dispatch(setViewEditorTabIndex(index))}>
              <HStack
                bg={'#fff'}
                gap={10}
                justify={'space-between'}
                position={'sticky'}
                top={0}
                zIndex={VIEW_EDITOR_DRAWER_HEADER_Z_INDEX}
              >
                <TabList>
                  <Tab>View Properties</Tab>
                  <Tab isDisabled={navIsBlocked}>Personnel</Tab>
                  <Tab isDisabled={navIsBlocked}>Assignments & Tallies</Tab>
                  <Tab isDisabled={navIsBlocked}>Viewer Configuration</Tab>
                </TabList>

                <DrawerActionButtons
                  isSaveDisabled={isSaveDisabled}
                  navigationIsBlocked={navIsBlocked}
                  onCancel={handleClose}
                  onSave={handleSave}
                  showNavigation={true}
                  tabIndex={tabIndex}
                />
              </HStack>
              <TabPanels>
                <TabPanel className={styles.panel}>
                  <ViewEditorBasicProperties
                    handleDepartmentOptionsChange={handleDepartmentChange}
                    handleTemplateOptionsChange={(selectedTemplateIds) =>
                      dispatch(setViewEditorDraftTemplateIds(selectedTemplateIds))
                    }
                  />
                </TabPanel>
                <TabPanel className={styles.panel}>
                  <ViewEditorPersonnel />
                </TabPanel>
                <TabPanel className={styles.panel}>
                  <ViewEditorAssignmentsTallies />
                </TabPanel>
                <TabPanel className={styles.panel}>
                  <ViewEditorViewerConfiguration />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ViewEditorDrawer;
